import { LoadingButton } from '@mui/lab';
import { Box, Divider, Grid, Stack, Typography } from '@mui/material';

import { FormProvider, RHFTextField } from 'src/components/hook-form';
import RhfDatePicker from 'src/components/hook-form/rhf-date-picker';
import Iconify from 'src/components/iconify';

import { useTableCollapse } from './table-collapse.hook';

interface TableCollapseProps {
  row: any;
  getData: () => void;
}

export default function TableCollapse({ row, getData }: TableCollapseProps) {
  const {
    methods,
    onSubmit,
    errors,
    handleButtonClick,
    handleBannerChange,
    handleThumbnailChange,
    hasSuccess,
    bannerInputRef,
    thumbnailInputRef,
    isSubmitting,
    values,
  } = useTableCollapse(row, getData);
  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3} direction="row">
        <Grid item xs={12} md={7}>
          <Stack spacing={4}>
            <Stack spacing={1}>
              <Typography fontWeight="500">Nome da Campanha/Promoção</Typography>
              <RHFTextField name="promotionName" variant="standard" fullWidth />
            </Stack>
            <Stack>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <Typography fontWeight="500">Dia Inicial e Final</Typography>
                    <RhfDatePicker name="startDate" dateValueFormat="L" />
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <Box height={24} />
                    <RhfDatePicker name="endDate" dateValueFormat="L" />
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
            <Stack spacing={1}>
              <Typography fontWeight="500">Resumo da Campanha/Promoção</Typography>
              <RHFTextField name="preview" variant="outlined" fullWidth multiline rows={5} />
              <Box height={12} />
              <Typography fontWeight="500">Link do Regulamento</Typography>
              <RHFTextField
                name="regulationUrl"
                variant="standard"
                fullWidth
                placeholder="Insira o link para o regulamento"
                inputProps={{ inputMode: 'url' }}
              />
              <Box height={12} />
              <Typography fontWeight="500">Lojas Participantes</Typography>
              {/* <RHFAutocomplete variant="standard" multiple name="categories" options={[]} /> */}
              <RHFTextField name="stores" variant="outlined" fullWidth multiline rows={5} />
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md="auto" alignSelf="stretch">
          <Divider orientation="vertical" />
        </Grid>
        <Grid item xs={12} md={2}>
          <Stack justifyContent="center" alignItems="center" spacing={4} height="100%">
            <Stack>
              <Typography typography="bold-bd-md">Banner</Typography>
              <Typography typography="body-sm" color="greyScale.300">
                Proporção no APP: 328px x 140px
              </Typography>
              <Typography typography="body-sm" color="greyScale.300">
                Resolução sugerida: 984px x 420px
              </Typography>
              <Box height={8} />
              {values.banner.url ? (
                <Box
                  height={58}
                  width={134}
                  component="img"
                  src={
                    values.banner.url.startsWith('http') || values.banner.url.startsWith('data:')
                      ? values.banner.url
                      : `data:${values.banner.type || 'image/png'};base64,${values.banner.url}`
                  }
                  sx={{ objectFit: 'cover' }}
                />
              ) : (
                <Iconify icon="ri:image-line" height={60} width={60} color="greyScale.300" />
              )}
              <Box height={16} />
              <Stack
                color="greyScale.300"
                direction="row"
                alignItems="center"
                justifyContent="start"
                spacing={1}
                onClick={() => handleButtonClick('banner')}
                sx={{ ':hover': { cursor: 'pointer' } }}
              >
                <Typography typography="body-sm">Escolher Arquivo</Typography>
                <Iconify icon="ri:download-2-line" />
              </Stack>
              {errors.banner && (
                <Typography typography="body-sm" color="error">
                  Selecione um arquivo!
                </Typography>
              )}
            </Stack>
            <Stack>
              <Typography typography="bold-bd-md">Thumbnail</Typography>
              <Typography typography="body-sm" color="greyScale.300">
                Proporção no APP: 145px x 80px. Resolução sugerida: 435px x 240px
              </Typography>
              <Box height={8} />
              {values.thumbnail?.url ? (
                <Box
                  height={58}
                  width={134}
                  component="img"
                  src={
                    values.thumbnail.url.startsWith('http') || values.thumbnail.url.startsWith('data:')
                      ? values.thumbnail.url
                      : `data:${values.thumbnail.type || 'image/png'};base64,${values.thumbnail.url}`
                  }
                  sx={{ objectFit: 'cover' }}
                />
              ) : (
                <Iconify icon="ri:image-line" height={60} width={60} color="greyScale.300" />
              )}
              <Box height={16} />
              <Stack
                color="greyScale.300"
                direction="row"
                alignItems="center"
                justifyContent="start"
                spacing={1}
                onClick={() => handleButtonClick('thumbnail')}
                sx={{ ':hover': { cursor: 'pointer' } }}
              >
                <Typography typography="body-sm">Escolher Arquivo</Typography>
                <Iconify icon="ri:download-2-line" />
              </Stack>
              {/* {errors.thumbnail && (
                <Typography typography="body-sm" color="error">
                  Selecione um arquivo!
                </Typography>
              )} */}
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md="auto" alignSelf="stretch">
          <Divider orientation="vertical" />
        </Grid>
        <Grid item xs={12} md>
          <Stack height="100%" width="100%" alignItems="center" justifyContent="center" spacing={1}>
            <LoadingButton loading={isSubmitting} type="submit" variant="contained" color="primary" fullWidth>
              Salvar
            </LoadingButton>
            {hasSuccess && (
              <Typography typography="body-md" color="feedback.success">
                Dados salvos com sucesso!
              </Typography>
            )}
          </Stack>
        </Grid>
      </Grid>
      <input
        ref={bannerInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleBannerChange}
        onClick={(e) => {
          (e.target as HTMLInputElement).value = '';
        }}
      />
      <input
        ref={thumbnailInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleThumbnailChange}
        onClick={(e) => {
          (e.target as HTMLInputElement).value = '';
        }}
      />
    </FormProvider>
  );
}
